import { useState } from 'react';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { STAT_IDS } from '@dcc/rules/domain/stats.ts';
import { DAMAGE_TYPES } from '@dcc/rules/rules/damage.ts';
import type { StatId } from '@dcc/rules/domain/stats.ts';
import { defaultCustomWeaponAttackState, type CustomWeaponAttackState } from './customItemFlow.ts';
import { GEAR_SLOTS, GEAR_SLOT_LABELS } from '@dcc/rules/rules/inventory.ts';
import type { GearSlot, ItemKind } from '@dcc/rules/content/types.ts';
import type {
  CustomItemEditorState,
  ItemModifierEditorState,
  ItemModifierKind,
} from '@dcc/rules/ui/customItemEditorModel.ts';
import {
  changeItemPlacement,
  emptyCustomItemEditorState,
  emptyItemModifierEditorState,
} from '@dcc/rules/ui/customItemEditorModel.ts';

const ITEM_KINDS: readonly ItemKind[] = ['gear', 'potion', 'scroll', 'container', 'misc'];

const MODIFIER_KINDS: readonly ItemModifierKind[] = [
  'stat',
  'skill-rank',
  'damage-resistance',
  'elemental-resistance',
  'vulnerability',
  'immunity',
  'evade',
  'move',
  'step',
  'max-mana',
  'advantage',
  'disadvantage',
];

const MODIFIER_LABELS: Record<ItemModifierKind, string> = {
  stat: 'Stat',
  'skill-rank': 'Skill Rank',
  'damage-resistance': 'Damage Resistance',
  'elemental-resistance': 'Elemental Resistance',
  vulnerability: 'Vulnerability',
  immunity: 'Immunity',
  evade: 'Evade',
  move: 'Move',
  step: 'Step',
  'max-mana': 'Max Mana',
  advantage: 'Advantage on all Checks',
  disadvantage: 'Disadvantage on all Checks',
};

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).replaceAll('-', ' ');
}

function needsAmount(kind: ItemModifierKind): boolean {
  return !['advantage', 'disadvantage', 'elemental-resistance', 'vulnerability', 'immunity'].includes(kind);
}

function needsDamageType(kind: ItemModifierKind): boolean {
  return kind === 'elemental-resistance' || kind === 'vulnerability' || kind === 'immunity';
}

export function CustomItemCreator(props: {
  disabled: boolean;
  problems: string[];
  onCreate: (state: CustomItemEditorState, weapon: CustomWeaponAttackState | null, quantity?: number) => Promise<boolean>;
}) {
  const [open, setOpen] = useState(false);
  const [copies, setCopies] = useState('1');
  const [state, setState] = useState<CustomItemEditorState>(emptyCustomItemEditorState());
  const [customWeapon, setCustomWeapon] = useState<CustomWeaponAttackState | null>(null);

  function patch(next: Partial<CustomItemEditorState>) {
    setState((current) => ({ ...current, ...next }));
  }

  function patchModifier(index: number, next: Partial<ItemModifierEditorState>) {
    setState((current) => ({
      ...current,
      modifiers: current.modifiers.map((row, i) => i === index ? { ...row, ...next } : row),
    }));
  }

  const heldHands = state.slot === 'hands' && state.handUse === 'held';
  const weaponValue = customWeapon !== null
    ? '__new_custom_weapon__'
    : state.weaponChoice === 'none'
      ? 'none'
      : state.weaponChoice === 'linked'
        ? state.weaponAttackId
        : '';
  const weaponAttacks = contentRegistry.attackContent.attacks
    .filter((attack) => attack.weaponUse !== null)
    .map((attack) => ({ attack, skill: contentRegistry.getSkill(attack.id) }))
    .sort((a, b) => (a.skill?.name ?? a.attack.id).localeCompare(b.skill?.name ?? b.attack.id));

  if (!open) {
    return (
      <section className="dcc-creator-card">
        <h2>Custom Item</h2>
        <p className="dcc-creator-help">Create a player-authored Item definition and add 1–99 copies to this crawler.</p>
        <button className="dcc-creator-secondary" disabled={props.disabled} onClick={() => setOpen(true)}>Create custom Item</button>
      </section>
    );
  }

  return (
    <section className="dcc-creator-card dcc-custom-item-editor">
      <div className="dcc-item-title">
        <h2>Custom Item</h2>
        <button className="dcc-creator-secondary" disabled={props.disabled} onClick={() => setOpen(false)}>Close</button>
      </div>

      <label className="dcc-creator-field">Copies (1–99)<input type="number" min={1} max={99} step={1} value={copies} disabled={props.disabled} onChange={e => setCopies(e.target.value)} /></label>
      <label className="dcc-creator-field">
        Name
        <input maxLength={60} value={state.name} disabled={props.disabled} onChange={(event) => patch({ name: event.target.value })} />
      </label>

      <label className="dcc-creator-field">Gold value per copy (optional)<input type="number" min={0} step={1} value={state.goldValue ?? ''} disabled={props.disabled} onChange={e => patch({ goldValue: e.target.value })} /></label>
      <label className="dcc-creator-field">
        Kind
        <select
          value={state.kind}
          disabled={props.disabled}
          onChange={(event) => {
            const kind = event.target.value as ItemKind;
            patch({ kind, ...(kind === 'potion' ? {} : { useMode: 'manual' as const }) });
          }}
        >
          {ITEM_KINDS.map((kind) => <option key={kind} value={kind}>{capitalise(kind)}</option>)}
        </select>
      </label>

      <label className="dcc-creator-field">
        Gear Slot
        <select
          value={state.slot}
          disabled={props.disabled}
          onChange={(event) => {
            const slot = event.target.value as GearSlot | '';
            setState((current) => changeItemPlacement(current, slot));
            if (slot !== 'hands') setCustomWeapon(null);
          }}
        >
          <option value="">None / carried only</option>
          {GEAR_SLOTS.map((slot) => <option key={slot} value={slot}>{GEAR_SLOT_LABELS[slot]}</option>)}
        </select>
      </label>

      {state.slot === 'hands' && (
        <label className="dcc-creator-field">
          Hands use
          <select
            value={state.handUse}
            disabled={props.disabled}
            onChange={(event) => {
              const handUse = event.target.value as 'held' | 'worn';
              setState((current) => changeItemPlacement(current, 'hands', handUse));
              if (handUse !== 'held') setCustomWeapon(null);
            }}
          >
            <option value="held">Held in a hand</option>
            <option value="worn">Worn on the hands</option>
          </select>
        </label>
      )}

      {heldHands && (
        <label className="dcc-creator-field">
          Physical weapon
          <select
            value={weaponValue}
            disabled={props.disabled}
            onChange={(event) => {
              const value = event.target.value;
              if (value === '__new_custom_weapon__') {
                setCustomWeapon(defaultCustomWeaponAttackState());
                patch({ weaponChoice: 'unselected', weaponAttackId: '' });
              } else {
                setCustomWeapon(null);
                if (value === '') patch({ weaponChoice: 'unselected', weaponAttackId: '' });
                else if (value === 'none') patch({ weaponChoice: 'none', weaponAttackId: '' });
                else patch({ weaponChoice: 'linked', weaponAttackId: value });
              }
            }}
          >
            <option value="">Choose…</option>
            <option value="none">Not a weapon</option>
            <option value="__new_custom_weapon__">Create a new unique weapon Attack…</option>
            {weaponAttacks.map(({ attack, skill }) => (
              <option key={attack.id} value={attack.id}>
                {skill?.name ?? attack.id} ({attack.weaponUse?.hands ?? 1} hand{attack.weaponUse?.hands === 2 ? 's' : ''})
              </option>
            ))}
          </select>
        </label>
      )}

      {heldHands && customWeapon !== null && (
        <div className="dcc-custom-modifiers">
          <div className="dcc-item-title"><strong>Unique weapon Attack</strong></div>
          <p className="dcc-creator-help">
            This creates a real Attack Skill for the weapon, so it appears on the Attack tab and rolls its own damage through Dice+.
          </p>

          <label className="dcc-creator-field">
            Attack name
            <input
              maxLength={60}
              value={customWeapon.attackName}
              placeholder={state.name.trim() || 'Defaults to Item name'}
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, attackName: event.target.value } : current)}
            />
          </label>

          <label className="dcc-creator-field">
            Weapon style
            <select
              value={customWeapon.style}
              disabled={props.disabled}
              onChange={(event) => {
                const style = event.target.value as 'melee' | 'ranged';
                setCustomWeapon((current) => current ? {
                  ...current,
                  style,
                  toHitStat: style === 'melee' ? 'str' : 'dex',
                  damageStat: style === 'melee' ? 'str' : null,
                  damageType: style === 'melee' ? 'slashing' : 'piercing',
                  rangeFeet: style === 'ranged' ? (current.rangeFeet || '30') : '',
                } : current);
              }}
            >
              <option value="melee">Melee</option>
              <option value="ranged">Ranged</option>
            </select>
          </label>

          <label className="dcc-creator-field">
            Starting Skill Rank
            <input
              type="number"
              min={1}
              step={1}
              value={customWeapon.startingRank}
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, startingRank: event.target.value } : current)}
            />
          </label>

          <label className="dcc-creator-field">
            Attack roll Stat
            <select
              value={customWeapon.toHitStat}
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, toHitStat: event.target.value as StatId } : current)}
            >
              {STAT_IDS.map((stat) => <option key={stat} value={stat}>{stat.toUpperCase()}</option>)}
            </select>
          </label>

          <label className="dcc-creator-field">
            Base damage dice
            <input
              value={customWeapon.damageDice}
              placeholder="1d6"
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, damageDice: event.target.value } : current)}
            />
          </label>

          <label className="dcc-creator-field">
            Damage Stat
            <select
              value={customWeapon.damageStat ?? ''}
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? {
                ...current,
                damageStat: event.target.value === '' ? null : event.target.value as StatId,
              } : current)}
            >
              <option value="">None</option>
              {STAT_IDS.map((stat) => <option key={stat} value={stat}>{stat.toUpperCase()}</option>)}
            </select>
          </label>

          <label className="dcc-creator-field">
            Damage Type
            <select
              value={customWeapon.damageType}
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, damageType: event.target.value as typeof current.damageType } : current)}
            >
              {DAMAGE_TYPES.map((type) => <option key={type} value={type}>{capitalise(type)}</option>)}
            </select>
          </label>

          <label className="dcc-creator-field">
            Hands required
            <select
              value={customWeapon.hands}
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, hands: Number(event.target.value) as 1 | 2 } : current)}
            >
              <option value={1}>One hand</option>
              <option value={2}>Two hands</option>
            </select>
          </label>

          {customWeapon.style === 'ranged' && (
            <label className="dcc-creator-field">
              Range (feet)
              <input
                type="number"
                min={1}
                step={1}
                value={customWeapon.rangeFeet}
                disabled={props.disabled}
                onChange={(event) => setCustomWeapon((current) => current ? { ...current, rangeFeet: event.target.value } : current)}
              />
            </label>
          )}

          <label className="dcc-creator-field">
            Extra damage dice at Rank 5 (optional)
            <input
              value={customWeapon.rank5AddedDamageDice}
              placeholder="e.g. 1d6"
              disabled={props.disabled}
              onChange={(event) => setCustomWeapon((current) => current ? { ...current, rank5AddedDamageDice: event.target.value } : current)}
            />
          </label>
        </div>
      )}

      <label className="dcc-custom-check">
        <input
          type="checkbox"
          checked={state.consumable}
          disabled={props.disabled}
          onChange={(event) => patch({ consumable: event.target.checked, ...(event.target.checked ? {} : { useMode: 'manual' as const }) })}
        />
        Used up when consumed
      </label>

      {state.kind === 'potion' && state.consumable && (
        <label className="dcc-creator-field">
          Automated use
          <select value={state.useMode} disabled={props.disabled} onChange={(event) => patch({ useMode: event.target.value as CustomItemEditorState['useMode'] })}>
            <option value="manual">Manual / special rules only</option>
            <option value="restore-mana-full">Restore Mana to full</option>
          </select>
        </label>
      )}

      <label className="dcc-creator-field">
        Description
        <textarea rows={3} maxLength={2000} value={state.description} disabled={props.disabled} onChange={(event) => patch({ description: event.target.value })} />
      </label>

      <label className="dcc-creator-field">
        Special Rules
        <textarea rows={4} maxLength={4000} value={state.notes} disabled={props.disabled} onChange={(event) => patch({ notes: event.target.value })} />
      </label>

      <div className="dcc-custom-modifiers">
        <div className="dcc-item-title">
          <strong>Mechanical bonuses</strong>
          <button
            className="dcc-creator-secondary"
            disabled={props.disabled}
            onClick={() => setState((current) => ({ ...current, modifiers: [...current.modifiers, emptyItemModifierEditorState()] }))}
          >Add modifier</button>
        </div>
        {state.modifiers.length === 0 && <p className="dcc-creator-help">Optional. Bonuses apply only while the Item is equipped.</p>}
        {state.modifiers.map((row, index) => (
          <div key={index} className="dcc-custom-modifier">
            <label className="dcc-creator-field">
              Modifier
              <select value={row.kind} disabled={props.disabled} onChange={(event) => patchModifier(index, { kind: event.target.value as ItemModifierKind })}>
                {MODIFIER_KINDS.map((kind) => <option key={kind} value={kind}>{MODIFIER_LABELS[kind]}</option>)}
              </select>
            </label>

            {row.kind === 'stat' && (
              <label className="dcc-creator-field">
                Stat
                <select value={row.stat} disabled={props.disabled} onChange={(event) => patchModifier(index, { stat: event.target.value as typeof row.stat })}>
                  {STAT_IDS.map((stat) => <option key={stat} value={stat}>{stat.toUpperCase()}</option>)}
                </select>
              </label>
            )}

            {row.kind === 'skill-rank' && (
              <label className="dcc-creator-field">
                Skill
                <select value={row.skillId} disabled={props.disabled} onChange={(event) => patchModifier(index, { skillId: event.target.value })}>
                  <option value="">Choose a Skill…</option>
                  {contentRegistry.allSkills().map((skill) => <option key={skill.id} value={skill.id}>{skill.name}</option>)}
                </select>
              </label>
            )}

            {needsDamageType(row.kind) && (
              <label className="dcc-creator-field">
                Damage Type
                <select value={row.damageType} disabled={props.disabled} onChange={(event) => patchModifier(index, { damageType: event.target.value as typeof row.damageType })}>
                  {DAMAGE_TYPES.map((type) => <option key={type} value={type}>{capitalise(type)}</option>)}
                </select>
              </label>
            )}

            {needsAmount(row.kind) && (
              <label className="dcc-creator-field">
                Amount
                <input value={row.amount} disabled={props.disabled} onChange={(event) => patchModifier(index, { amount: event.target.value })} inputMode="numeric" />
              </label>
            )}

            <button
              className="dcc-danger"
              disabled={props.disabled}
              onClick={() => setState((current) => ({ ...current, modifiers: current.modifiers.filter((_, i) => i !== index) }))}
            >Remove modifier</button>
          </div>
        ))}
      </div>

      {props.problems.length > 0 && (
        <ul className="dcc-creator-problems dcc-creator-warn">
          {props.problems.map((problem, index) => <li key={`${index}-${problem}`}>{problem}</li>)}
        </ul>
      )}

      <div className="dcc-creator-nav">
        <button
          disabled={props.disabled || state.name.trim() === '' || !Number.isInteger(Number(copies)) || Number(copies) < 1 || Number(copies) > 99}
          onClick={() => {
            void props.onCreate(state, customWeapon, Number(copies)).then((created) => {
              if (!created) return;
              setState(emptyCustomItemEditorState());
              setCustomWeapon(null);
              setOpen(false);
            });
          }}
        >Create & add copies</button>
      </div>
    </section>
  );
}
